<template>
  <b-modal
    ref="userappModal"
    size="xxl"
    centered
    modal-class="configuration-modal-tabs userapp-modal"
    static
    hide-footer
    @hide="onHide()"
  >
    <div
      slot="modal-title"
      class="d-flex align-items-center"
    >
      {{ actionTitle }}
      {{ $tc('configuration.sections.users', 1).toLowerCase() }}
    </div>
    <b-row class="px-0">
      <b-col class="px-2 pt-1 pb-2">
        <div>
          <b-tabs
            v-model="activeTabIndex"
            pills
            card
            vertical
            class="vt-tabs--pills"
          >
            <b-tab active>
              <template slot="title">
                <i
                  class="far fa-list-ul mr-2"
                  aria-hidden="true"
                />
                {{ $t('configuration.modal.basicData') }}
              </template>
              <b-row class="px-1 pt-1 pb-3">
                <b-col class="mt-3">
                  <b-row>
                    <b-col>
                      <div class="form-card">
                        <span class="ap-title ap-title__section"> <i
                          class="far fa-user mr-2 mb-2"
                          aria-hidden="true"
                        />{{ $tc('configuration.modal.user.userData') }}</span>
                        <b-row class="mt-2">
                          <b-col
                            cols="6"
                            class="mb-3"
                          >
                            <div class="form-label">
                              <label>{{ $tc('configuration.grid.userType') }} *</label>
                            </div>
                            <vt-combo-select
                              v-model="internalUserapp.userType"
                              v-validate="'required'"
                              :options="userTypeOptions"
                              track-by="value"
                              :custom-label="localizedUserType"
                              :allow-empty="false"
                              @input="setUserType($event)"
                            />
                          </b-col>
                          <b-col
                            cols="6"
                            class="mb-3"
                          >
                            <div class="form-label">
                              <label>{{ $tc('configuration.modal.user.selectPerson') }} *</label>
                            </div>
                            <vt-combo-select
                              ref="personSelected"
                              v-model="userPerson"
                              v-validate="'required'"
                              :class="{ requiredField: showErrors && !userPerson }"
                              :allow-empty="false"
                              :option-loader="searchService"
                              label="fullName"
                              ajax-search
                              :internal-search="false"
                            />
                          </b-col>
                          <b-col cols="12">
                            <hr>
                          </b-col>
                        </b-row>
                        <b-row>
                          <b-col
                            cols="6"
                            class="px-0"
                          >
                            <b-col
                              cols="12"
                              class="mb-3"
                            >
                              <div class="form-label">
                                <label>{{ $tc('configuration.grid.username') }} *</label>
                              </div>
                              <vt-input
                                v-model="internalUserapp.username"
                                v-validate="'required'"
                                :autocomplete="false"
                                :class="{ requiredField: showErrors && !internalUserapp.username }"
                                class="form-control-sm"
                              />
                            </b-col>
                            <b-col
                              v-if="!internalUserapp.id"
                              cols="12"
                              class="mb-3"
                            >
                              <div class="form-label">
                                <label>{{ $tc('configuration.modal.user.password') }}</label>
                              </div>
                              <b-input-group
                                class="password-form"
                                :class="{ 'password-error': newPasswordErrorMsg }"
                                size="sm"
                              >
                                <template #append>
                                  <b-input-group-text
                                    class="show-hide-password"
                                    @click="showPassword = !showPassword"
                                  >
                                    <span
                                      v-if="!showPassword"
                                      class="far fa-eye"
                                    />
                                    <span
                                      v-if="showPassword"
                                      class="far fa-eye-slash"
                                    />
                                  </b-input-group-text>
                                </template>
                                <vt-input
                                  ref="changePassword"
                                  v-model="internalUserapp.password"
                                  :type="showPassword ? 'text' : 'password'"
                                  :name="$tc('configuration.modal.user.changePassword')"
                                  :placeholder="$tc('configuration.modal.user.passwordPlaceholder')"
                                  :autocomplete="false"
                                  class="form-control-sm"
                                />
                              </b-input-group>
                              <small
                                v-if="newPasswordErrorMsg"
                                class="error"
                              >{{ newPasswordErrorMsg }}</small>
                            </b-col>
                            <b-col cols="12">
                              <div class="switch-btn mt-1 p-0 d-flex align-items-center">
                                <span
                                  v-if="internalUserapp.blocked"
                                  class="danger-color"
                                >{{ $tc('configuration.modal.user.blockedUser') }}</span>
                              </div>
                            </b-col>
                          </b-col>
                          <b-col
                            :key="refreshProfileImageKey"
                            cols="5"
                            class="d-flex flex-column justify-content-center pr-0"
                          >
                            <div class="d-flex justify-content-center mb-3">
                              <img
                                v-if="userLastProfileImage"
                                class="card-photo"
                                :src="userLastProfileImage"
                                alt="user profile"
                                aria-hidden="true"
                              >
                              <img
                                v-else
                                class="card-photo blank"
                                src="@/img/user-img.png"
                                alt="default profile"
                                aria-hidden="true"
                              >
                            </div>
                            <div class="text-center">
                              <b-form-group>
                                <div>
                                  <label v-if="!userLastProfileImage"><i
                                    class="far fa-upload mr-2"
                                    aria-hidden="true"
                                  />{{ $tc('component.studyRequest.addFileModal.labelFile') }}</label>
                                  <label v-else><i
                                    class="far fa-check green-radiant mr-2"
                                    aria-hidden="true"
                                  />{{ $tc('configuration.modal.user.pictureSelected') }}</label>
                                </div>
                                <b-button
                                  id="pick-configUserapp-avatar"
                                  class="btn__outline mt-2"
                                  size="sm"
                                >
                                  {{ $t('configuration.modal.user.browseTextButton') }}
                                </b-button>
                                <b-button
                                  v-if="userLastProfileImage"
                                  class="btn__outline mt-2 ml-1"
                                  size="sm"
                                  @click="removeUserProfileImage"
                                >
                                  <i
                                    class="far fa-trash-alt mr-2"
                                    aria-hidden="true"
                                  />{{ $tc('global.delete') }}
                                </b-button>
                                <avatar-cropper
                                  :upload-handler="cropperHandler"
                                  :labels="{
                                    submit: $tc('global.accept'),
                                    cancel: $tc('global.cancel')
                                  }"
                                  trigger="#pick-configUserapp-avatar"
                                />
                              </b-form-group>
                            </div>
                          </b-col>
                        </b-row>
                      </div>
                    </b-col>
                  </b-row>
                  <b-row class="mt-3">
                    <b-col>
                      <div class="form-card">
                        <span class="ap-title ap-title__section"><i
                          class="far fa-flag mr-2 mb-2"
                          aria-hidden="true"
                        />{{ $tc('configuration.modal.user.languages') }}</span>
                        <b-row class="mt-2" />
                        <b-row>
                          <b-col
                            cols="6"
                            class="mb-3"
                          >
                            <div class="form-label">
                              <label>{{ $tc('userConfiguration.appLanguage') }} *</label>
                            </div>
                            <vt-combo-select
                              v-model="internalUserapp.language"
                              v-validate="'required'"
                              :options="appLanguages"
                              :class="{ requiredField: showErrors && !internalUserapp.language }"
                              label="name"
                              :allow-empty="false"
                              data-vv-scope="language"
                              data-vv-name="name"
                            />
                          </b-col>
                          <b-col
                            cols="6"
                            class="mb-3"
                          >
                            <div class="form-label">
                              <label>{{ $t('printService.printerLang') }} *</label>
                            </div>
                            <vt-combo-select
                              v-model="internalUserapp.reportingLanguage"
                              v-validate="'required'"
                              :options="reportingLanguages"
                              :class="{ requiredField: showErrors && !internalUserapp.reportingLanguage }"
                              label="name"
                              :allow-empty="false"
                              data-vv-scope="reportingLanguage"
                              data-vv-name="name"
                            />
                          </b-col>
                        </b-row>
                      </div>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col
                      cols="12"
                      class="my-2 d-flex justify-content-end"
                    >
                      <div class="switch-btn mt-1 p-0 d-flex align-items-center">
                        <span
                          v-if="internalUserapp.active"
                          class="mr-2"
                        >{{ $t('configuration.actions.active') }}</span>
                        <span
                          v-else
                          class="mr-2"
                        >{{ $t('configuration.actions.inactive') }}</span>
                        <input
                          id="toggle"
                          v-model="internalUserapp.active"
                          type="checkbox"
                          class="offscreen"
                          aria-hidden="true"
                        >
                        <label
                          for="toggle"
                          class="switch mb-0"
                        />
                      </div>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
              <div
                class=" footer-area tab-footer d-flex end py-2 align-items-center"
                :class="{
                  'justify-content-end': !internalUserapp.id,
                  'justify-content-between': internalUserapp.id
                }"
              >
                <div v-if="internalUserapp.id">
                  <b-button
                    v-if="isUserPetitioner"
                    :disabled="!userPerson"
                    :to="{
                      name: 'PeoplePetitioners',
                      params: { petitionerId: userPerson ? userPerson.id : null }
                    }"
                    class="btn__link"
                  >
                    <span>{{ $t('configuration.modal.goToPetitioner') }}</span>
                  </b-button>
                  <b-button
                    v-if="isUserPersonnel"
                    :disabled="!userPerson"
                    :to="{
                      name: 'PeoplePersonnel',
                      params: {
                        personnelId: userPerson ? userPerson.id : null
                      }
                    }"
                    variant="link"
                    class="secondary-button"
                  >
                    <span>{{ $t('configuration.modal.goToPersonnel') }}</span>
                  </b-button>
                </div>
                <div>
                  <b-button
                    size="sm"
                    class="btn--primary"
                    @click="update"
                  >
                    <i
                      class="fal fa-check mr-2"
                      aria-hidden="true"
                    />
                    {{ actionTitle }}
                  </b-button>
                </div>
              </div>
            </b-tab>
            <b-tab v-if="showPasswordTab">
              <template slot="title">
                <i
                  class="far fa-lock mr-2"
                  aria-hidden="true"
                />
                {{ $t('configuration.modal.user.password') }}
              </template>
              <b-row
                style=""
                class="px-1 pt-1 pb-3"
              >
                <b-col class="mt-3">
                  <b-row>
                    <b-col>
                      <div class="form-card">
                        <span class="ap-title ap-title__section"><i
                          class="far fa-key mr-2 mb-2"
                          aria-hidden="true"
                        />{{ $t('configuration.modal.user.password') }}</span>
                        <b-row class="mt-2">
                          <b-col
                            cols="6"
                            class="mb-3"
                          >
                            <div class="form-label">
                              <label>{{ $t('configuration.modal.user.changePassword') }}</label>
                            </div>
                            <form autocomplete="off">
                              <b-input-group
                                class="password-form"
                                :class="{ 'password-error': newPasswordErrorMsg }"
                                size="sm"
                              >
                                <template #append>
                                  <b-input-group-text
                                    class="show-hide-password"
                                    @click="showPassword = !showPassword"
                                  >
                                    <span
                                      v-if="!showPassword"
                                      class="far fa-eye"
                                    />
                                    <span
                                      v-if="showPassword"
                                      class="far fa-eye-slash"
                                    />
                                  </b-input-group-text>
                                </template>
                                <vt-input
                                  ref="changePassword"
                                  v-model="internalUserapp.password"
                                  :type="showPassword ? 'text' : 'password'"
                                  :name="$t('configuration.modal.user.changePassword')"
                                  :placeholder="$t('configuration.modal.user.passwordPlaceholder')"
                                  class="form-control-sm"
                                />
                              </b-input-group>
                              <small
                                v-if="newPasswordErrorMsg"
                                class="error"
                              >{{ newPasswordErrorMsg }}</small>
                            </form>
                          </b-col>
                        </b-row>
                        <b-row class="mb-2">
                          <b-col class="d-flex justify-content-end">
                            <b-button
                              size="sm"
                              class="btn--primary"
                              @click="changePassword"
                            >
                              <i
                                class="fal fa-check mr-2"
                                aria-hidden="true"
                              />
                              {{ $t('configuration.modal.user.changePassword') }}
                            </b-button>
                          </b-col>
                        </b-row>
                      </div>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
            </b-tab>
            <b-tab :disabled="!internalUserapp.id">
              <template slot="title">
                <i
                  class="far fa-pencil-ruler mr-2"
                  aria-hidden="true"
                />
                {{ $tc('configuration.sections.roles', 2) }}
              </template>
              <userapp-roles
                v-if="activeTabIndex === ROLES_TAB"
                ref="userappRoles"
                :userapp-id="internalUserapp.id"
                :laboratories="laboratories"
                :userapp-roles="userappRoles"
              />
            </b-tab>
            <b-tab :disabled="!internalUserapp.id">
              <template slot="title">
                <i
                  class="far fa-user-lock mr-2"
                  aria-hidden="true"
                />
                {{ $tc('configuration.modal.operation', 2) }}
              </template>
              <userapp-operations
                v-if="activeTabIndex === OPERATIONS_TAB"
                ref="userappOperations"
                :laboratories="laboratories"
                :userapp-operations="userappOperations"
                :userapp-id="internalUserapp.id"
              />
            </b-tab>
          </b-tabs>
        </div>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import { cloneDeep, isEmpty } from 'lodash'
import { ASSOCIATION_TYPE } from '@/api/resources/configuration/peopleManagement/AssociationTypes'
import RoleConfigurationServices from '@/api/resources/configuration/peopleManagement/role/RoleConfigurationServices'
import UserappRoleConfigurationServices from '@/api/resources/configuration/peopleManagement/userapp/UserappRoleConfigurationServices'
import OperationConfigurationServices from '@/api/resources/configuration/peopleManagement/operation/OperationConfigurationServices'
import UserappOperationConfigurationServices from '@/api/resources/configuration/peopleManagement/userapp/UserappOperationConfigurationServices'
import PersonnelConfigurationServices from '@/api/resources/configuration/peopleManagement/personnel/PersonnelConfigurationServices'
import PetitionerConfigurationServices from '@/api/resources/configuration/peopleManagement/petitioner/PetitionerConfigurationServices'
import UserappConfigurationServices from '@/api/resources/configuration/peopleManagement/userapp/UserappConfigurationServices'
import AttachmentResource from '@/api/resources/attachment/attachmentResource'
import languageService from '@/api/resources/common/languageService'
import AvatarCropper from 'vue-avatar-cropper'
import UserappRoles from '@/modules/configurationModule/detail/peopleManagement/userapp/UserappRoles'
import UserappOperations from '@/modules/configurationModule/detail/peopleManagement/userapp/UserappOperations'
import associationsMixin from '@/modules/configurationModule/detail/peopleManagement/utils/associationsMixin'
import { auth } from '@/api/auth'
import { StoreUtils } from '@/shared/util/store-utils'
import { UserType, UserTypeTranslationKeys } from '@/api/resources/common/initialization/common-initialization.dto.ts'
import CommonPatientServices from '@/api/resources/common/commonPatientServices'
import { enumKeys } from '@/utils/enums.ts'
import { GridUtils } from '@/shared/util/grid-utils'

const DATA_TAB = 0
const PASSWORD_TAB = 1
const ROLES_TAB = 2
const OPERATIONS_TAB = 3

const ROLES_TAB_WITHOUT_PASSWORD_TAB = 1
const OPERATIONS_TAB_WITHOUT_PASSWORD_TAB = 2

export default {
    name: 'UserappModal',
    components: { UserappOperations, UserappRoles, AvatarCropper },
    mixins: [associationsMixin],
    props: {
        actionTitle: String
    },
    data() {
        return {
            activeTabIndex: DATA_TAB,
            internalUserapp: {
                petitioner: null,
                personnel: null
            },
            userappRoles: [],
            userappOperations: [],
            attachmentResource: new AttachmentResource(),
            reportingLanguages: [],
            appLanguages: [],
            refreshProfileImageKey: 0,
            repeatedNewPasswordErrorMsg: null,
            newPasswordErrorMsg: null,
            repeatedNewPassword: null,
            showErrors: false,
            userLastProfileImage: null,
            showPassword: false,
            canChangePassword: true
        }
    },
    computed: {
        isUserPetitioner() {
            return this.internalUserapp.userType === UserType.PETITIONER
        },
        isUserPersonnel() {
            return this.internalUserapp.userType === UserType.PERSONNEL
        },
        userTypeOptions() {
            return enumKeys(UserType)
        },
        laboratories() {
            // TODO Debería filtrarse por los permisos del usuario para crear/eliminar PersonnelResponsibilities en cada laboratorio
            const storeLaboratories = [...StoreUtils.laboratories]
            storeLaboratories.unshift({
                labName: this.$tc('global.all'),
                all: true
            })
            return storeLaboratories
        },
        passwordMinimumLength() {
            return this.$store.getters.parameters.global?.passwordMinimumLength
        },
        passwordMaximumLength() {
            return this.$store.getters.parameters.global?.passwordMaximumLength
        },
        userPerson: {
            get: function() {
                switch (this.internalUserapp.userType) {
                case UserType.PERSONNEL:
                    return this.internalUserapp.personnel
                case UserType.PATIENT:
                    return this.internalUserapp.patient
                case UserType.PETITIONER:
                    return this.internalUserapp.petitioner
                }
                return null
            },
            set: function(value) {
                switch (this.internalUserapp.userType) {
                case UserType.PERSONNEL:
                    this.internalUserapp.personnel = value
                    break
                case UserType.PATIENT:
                    this.internalUserapp.patient = value
                    break
                case UserType.PETITIONER:
                    this.internalUserapp.petitioner = value
                    break
                }
            }
        },
        PASSWORD_TAB() {
            return PASSWORD_TAB
        },
        ROLES_TAB() {
            return this.showPasswordTab ? ROLES_TAB : ROLES_TAB_WITHOUT_PASSWORD_TAB
        },
        OPERATIONS_TAB() {
            return this.showPasswordTab ? OPERATIONS_TAB : OPERATIONS_TAB_WITHOUT_PASSWORD_TAB
        },
        ASSOCIATION_TYPE() {
            return ASSOCIATION_TYPE
        },
        showPasswordTab() {
            return this.internalUserapp.id && this.canChangePassword
        }
    },
    watch: {
        async activeTabIndex() {
            if (this.activeTabIndex === this.PASSWORD_TAB && this.showPasswordTab) {
                this.$refs.changePassword.focus()
            } else if (this.activeTabIndex === this.ROLES_TAB) {
                await this.setUserappRoles(this.internalUserapp)
            } else if (this.activeTabIndex === this.OPERATIONS_TAB) {
                await this.setUserappOperations(this.internalUserapp)
            }
        }
    },
    beforeMount() {
        this.initUserapp()
    },
    methods: {
        localizedUserType(userType) {
            const translation = UserTypeTranslationKeys.find(userTypeTranslation => userTypeTranslation.code === userType)
            return this.$tc(`${translation.translationKey}`)
        },
        removeUserProfileImage() {
            this.internalUserapp.removeUserLastProfileImage = true
            this.userLastProfileImage = null
            this.refreshProfileImageKey++
        },
        initUserapp(userapp) {
            if (userapp) {
                this.internalUserapp = cloneDeep(userapp)
                this.internalUserapp.userType = userapp.userType.enumValue
            } else {
                this.internalUserapp = {
                    active: true,
                    userType: UserType.PERSONNEL,
                    petitioner: null,
                    personnel: null,
                    patient: null,
                    selectedProfileImageBlob: null,
                    removeUserLastProfileImage: false
                }
            }
        },
        async initRolesAndOperations() {
            await this.setUserappRoles(this.internalUserapp)
            await this.setUserappOperations(this.internalUserapp)
        },
        cropperHandler(cropper) {
            this.userLastProfileImage = cropper.getCroppedCanvas({ width: 100, height: 100 }).toDataURL()
            cropper.getCroppedCanvas({ width: 100, height: 100 }).toBlob(async blob => {
                this.internalUserapp.selectedProfileImageBlob = blob
            })
            this.refreshProfileImageKey++
        },
        async changePassword() {
            if (this.validPasswords()) {
                this.updateLoading(true)
                const [err] = await this.$_toCall(UserappConfigurationServices.changePasswordAndUnlock(this.internalUserapp.id, this.internalUserapp.password), true)
                this.updateLoading(false)
                if (err) {
                    return
                }
                this.showToastSuccessGlobal(this.$tc('configuration.successModalMessages.passwordChanged'))
                this.internalUserapp.blocked = false
            }
        },
        async setUserType(userType) {
            this.internalUserapp.personnel = null
            this.internalUserapp.petitioner = null
            this.internalUserapp.patient = null

            this.$refs.personSelected.clearInternalOptions()
            this.internalUserapp.userType = userType

            if (this.internalUserapp.id) {
                this.userappOperations = []
                this.userappRoles = []
                await this.setUserappRoles(this.internalUserapp)
                await this.setUserappOperations(this.internalUserapp)
            }
        },
        async searchService(query) {
            let content = []
            if (query && query.length > 2) {
                let err, response
                switch (this.internalUserapp.userType) {
                case UserType.PETITIONER:
                    [err, response] = await this.toCall(
                        PetitionerConfigurationServices.getPetitionersByName({
                            nameFragments: query,
                            size: 20
                        })
                    )
                    content = response.content
                    break
                case UserType.PERSONNEL:
                    [err, response] = await this.toCall(
                        PersonnelConfigurationServices.getPersonnelByName({
                            query: query,
                            size: 20
                        })
                    )
                    content = response.content
                    break
                case UserType.PATIENT:
                    [err, response] = await this.toCall(
                        CommonPatientServices.search({
                            query: query,
                            size: 20
                        })
                    )
                    content = response.map(patient => ({
                        id: patient.id,
                        fullName: patient.label
                    }))
                    break
                }
                if (err) {
                    this.showErrorModalGlobal()
                    return []
                }
            }
            return content
        },
        async update() {
            let formValidated = await this.$validator.validate()
            let validPassword = this.validPasswords()
            if (!formValidated) {
                this.showErrorModalGlobal(this.$t('global.incompleteForm'))
                this.showErrors = true
                return
            }
            if (this.internalUserapp.id) {
                this.$emit('update', this.internalUserapp)
            } else {
                if (validPassword) this.$emit('create', this.internalUserapp)
            }
        },
        onHide() {
            this.clearInternalData()
        },
        clearInternalData() {
            this.clearErrors()
            this.userappRoles = []
            this.userappOperations = []
            this.internalUserapp = {}
            this.userLastProfileImage = null
            this.removeUserLastProfileImage = false
            this.showErrors = false
        },
        async getLastUserProfileImage(selectedUserapp) {
            this.updateLoading(true)
            const [err, response] = await this.toCall(UserappConfigurationServices.lastProfileImage(selectedUserapp.id))
            this.updateLoading(false)
            if (err) {
                if (err.response.status !== 404) {
                    this.showErrorModalGlobal(err.message)
                }
                return err
            }
            this.userLastProfileImage = await this.attachmentResource.fileToBase64(response)
        },
        async getAppLanguages() {
            this.updateLoading(true)
            const [err, response] = await this.toCall(languageService.findAllActiveAndUiEnabled())
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
            }
            this.appLanguages = response
        },
        async getReportingLanguages() {
            this.updateLoading(true)
            const [err, response] = await this.toCall(languageService.findAllActiveAndReportingEnabled())
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
            }
            this.reportingLanguages = response
        },
        async show(selectedUserapp, associationType) {
            await this.getAppLanguages()
            await this.getReportingLanguages()
            if (selectedUserapp && !isEmpty(selectedUserapp)) {
                this.initUserapp(selectedUserapp)
                if (this.internalUserapp.id) {
                    await this.getLastUserProfileImage(selectedUserapp)
                    switch (this.internalUserapp.userType) {
                    case UserType.PERSONNEL:
                        this.canChangePassword = await auth.canChangePassword(this.internalUserapp.personnel.defaultLaboratory.labId)
                        break
                    case UserType.PATIENT:
                        this.canChangePassword = false
                        break
                    case UserType.PETITIONER:
                        this.canChangePassword = !(await auth.globalExternalAuthentication())
                        break
                    }
                }
            } else {
                this.initUserapp()
            }

            let targetTab = DATA_TAB
            if (associationType) {
                if (associationType.key === this.ASSOCIATION_TYPE.ROLE.key) {
                    targetTab = ROLES_TAB
                } else if (associationType.key === this.ASSOCIATION_TYPE.OPERATION.key) {
                    targetTab = OPERATIONS_TAB
                }
            }
            this.activeTabIndex = targetTab

            this.$refs.userappModal.show()
            this.refreshProfileImageKey++
        },
        async setUserappRoles(userapp) {
            const filterModel = {
                userType: GridUtils.getSetFilterModel([userapp.userType])
            }
            this.updateLoading(true)
            const [err, response] = await this.toCall(
                Promise.all([
                    RoleConfigurationServices.paginator(filterModel, {
                        size: 1000
                    }),
                    UserappRoleConfigurationServices.getUserappRolesByUserappId(userapp.id)
                ])
            )
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            const roles = response[0].content
            const userappRoles = response[1]
            let parsedUserappRoles = []
            roles.forEach(userappRole => {
                const userappRoleParsed = {
                    [this.ASSOCIATION_TYPE.ROLE.key]: userappRole
                }
                const parsedAssociation = this.mapAssociation(userappRoleParsed, this.ASSOCIATION_TYPE.ROLE.key)
                this.setAssociationLaboratories(parsedAssociation, userappRoles, this.ASSOCIATION_TYPE.ROLE.key, this.laboratories)
                parsedUserappRoles.push(parsedAssociation)
            })
            this.userappRoles = cloneDeep(parsedUserappRoles)
        },
        async setUserappOperations(userapp) {
            let filterModel = {
                userType: GridUtils.getSetFilterModel([userapp.userType])
            }
            this.updateLoading(true)
            const [err, response] = await this.toCall(
                Promise.all([
                    OperationConfigurationServices.paginator(filterModel, {
                        size: 1000
                    }),
                    UserappOperationConfigurationServices.getUserappOperationsByUserappId(userapp.id)
                ])
            )
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            const operations = response[0].content
            const userappOperations = response[1]
            let parsedUserappOperations = []
            operations.forEach(userappOperation => {
                const operationParsed = {
                    [this.ASSOCIATION_TYPE.OPERATION.key]: userappOperation,
                    [this.ASSOCIATION_TYPE.ROLE.key]: this.getAssignedRoleOperation(userappOperation, userappOperations)
                }
                const parsedAssociation = this.mapAssociation(operationParsed, this.ASSOCIATION_TYPE.OPERATION.key)
                this.setAssociationLaboratories(parsedAssociation, userappOperations, this.ASSOCIATION_TYPE.OPERATION.key, this.laboratories)
                parsedUserappOperations.push(parsedAssociation)
            })
            this.userappOperations = cloneDeep(parsedUserappOperations)
        },
        getAssignedRoleOperation(userappOperation, userappAssociations) {
            const operation = userappAssociations.find(association => association.operation.id === userappOperation.id)
            return operation ? operation.role : null
        },
        mapAssociation(association) {
            let parsedAssociation = cloneDeep(association)
            parsedAssociation.laboratories = this.laboratories.map(lab => {
                let parsedLab = {}
                parsedLab.labId = lab ? lab.labId : null
                parsedLab.assigned = false
                parsedLab.roles = []
                if (lab && lab.all) parsedLab.all = lab.all
                return parsedLab
            })
            return parsedAssociation
        },
        validPasswords() {
            this.clearErrors()
            let valid = true
            this.newPasswordErrorMsg = this.checkNewPassword(this.internalUserapp.password)
            if (this.newPasswordErrorMsg) {
                valid = false
            }
            return valid
        },
        clearErrors() {
            this.newPasswordErrorMsg = null
        },
        checkNewPassword(password) {
            let message = ''
            if (password) {
                if (password.length < this.passwordMinimumLength) {
                    message = this.$t('userConfiguration.passwordMinimumLengthError', {
                        minimumLength: this.passwordMinimumLength
                    })
                }
                if (password.length > this.passwordMaximumLength) {
                    message = this.$t('userConfiguration.passwordMaximumLengthError', {
                        maximumLength: this.passwordMaximumLength
                    })
                }
            }
            return message
        },
        hide() {
            this.$refs.userappModal.hide()
        }
    }
}
</script>

<style lang="scss">
.requiredField {
    border-color: red !important;
}
.requiredField .multiselect__tags {
    border-color: red !important;
}
.userapp-modal .modal-body {
    border-radius: 0.3rem;
}
.userapp-modal .tab-content {
    height: calc(100vh - 305px);
    overflow-y: auto;
}
.userapp-modal .tab-operations .bottom-line {
    width: 100% !important;
}
@media only screen and (max-width: 1366px) and (min-width: 1360px) {
    .userapp-modal .tab-content {
        height: calc(100vh - 160px);
    }
}
.card-photo {
    width: 140px !important;
    border-radius: 5px;
}
.card-photo.blank {
    opacity: 0.5;
}
</style>
